// -----------------------------------------------------------------------------
// Wires the chat SignalR `MessageReceived` event into the desktop
// Notifications API. Mounted once via the topbar `AppNotifications`
// component so it runs on every authenticated route.
//
// Suppression rules (matches the user's choice from the prompt):
//   - never notify on your own messages
//   - never notify for muted channels
//   - never notify when the user is actively viewing that channel
//     (signalled by `chat.activeChannelId` in sessionStorage + the tab
//     being focused). Background tabs always get a notification.
//
// Resolves author + channel labels from the existing React Query caches
// so the notification title is readable without an extra fetch. If the
// caches are cold we fall back to "New chat message" and let the user
// click through.
// -----------------------------------------------------------------------------

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../contexts/AuthContext";
import { useSignalR } from "../contexts/SignalRContext";
import { chatQueryKeys } from "../data/useChat";
import { fullNameFor } from "../utils/chatHelpers";
import {
  IChatChannel,
  IChatMessage,
  IChatUser,
} from "../models/Chat";
import { DefaultResponseWithData } from "../models/system";
import useDesktopNotifications, {
  UseDesktopNotifications,
} from "./useDesktopNotifications";

export const ACTIVE_CHANNEL_STORAGE_KEY = "chat.activeChannelId";

export default function useChatDesktopNotifications(): UseDesktopNotifications {
  const desktop = useDesktopNotifications();
  const { chatConnection } = useSignalR();
  const queryClient = useQueryClient();
  const { userId: meId } = useAuth();
  const navigate = useNavigate();

  // Refs hold the latest values without re-binding the SignalR handler
  // on every render - the listener attaches once per chatConnection
  // lifecycle and reads through these refs.
  const desktopRef = useRef(desktop);
  desktopRef.current = desktop;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const meIdRef = useRef(meId);
  meIdRef.current = meId;

  useEffect(() => {
    if (!chatConnection) return;

    const handler = (msg: IChatMessage) => {
      const me = meIdRef.current;
      if (!me || msg.authorId === me) return;
      if (!desktopRef.current.canShow) return;

      // Tab is focused AND the user is currently looking at this
      // channel - they'll see the message in-context, no need to
      // double-notify.
      const activeChannelId =
        typeof window !== "undefined"
          ? sessionStorage.getItem(ACTIVE_CHANNEL_STORAGE_KEY)
          : null;
      const focused =
        typeof document !== "undefined" && document.hasFocus();
      if (focused && activeChannelId === msg.channelId) return;

      const channelsCache = queryClient.getQueryData<
        DefaultResponseWithData<IChatChannel[]>
      >(chatQueryKeys.channels);
      const channel = channelsCache?.data?.find((c) => c.id === msg.channelId);
      if (channel?.muted) return;

      const usersCache = queryClient.getQueryData<
        DefaultResponseWithData<IChatUser[]>
      >(chatQueryKeys.users);
      const author = usersCache?.data?.find((u) => u.id === msg.authorId);

      const authorName = author ? fullNameFor(author) : "Someone";
      const channelLabel =
        channel?.kind === "channel" && channel.name
          ? `#${channel.name}`
          : channel?.kind === "dm"
            ? null
            : "chat";

      const title =
        channelLabel === null
          ? authorName
          : `${authorName} in ${channelLabel}`;

      // Attachment-only messages have an empty body — show a readable
      // stand-in instead of a blank notification.
      const body =
        (msg.body ?? "").trim() ||
        (msg.attachments?.length ? "📎 Attachment" : "");
      const truncated =
        body.length > 140 ? `${body.slice(0, 137).trim()}…` : body;

      desktopRef.current.show({
        title,
        body: truncated || undefined,
        // Channel-tagged notifications coalesce in the OS tray so a
        // burst of messages in the same channel replaces the previous
        // notification rather than stacking five copies.
        tag: `chat:${msg.channelId}`,
        onClick: () => {
          navigateRef.current(`/chat?channel=${msg.channelId}`);
        },
      });
    };

    chatConnection.on("MessageReceived", handler);
    return () => {
      chatConnection.off("MessageReceived", handler);
    };
  }, [chatConnection, queryClient]);

  return desktop;
}
